/**
 * FE-034 — cookie consent (Figma `Cookies` 39:41637: desktop 39:41638, mobile 39:41647).
 * The choice lives in localStorage; the backend is not involved.
 */

export const FAIR_COOKIE_CONSENT_STORAGE_KEY = 'fair.cookieConsent'

/** Figma annotation: bottom centre of the viewport, 40px from the bottom edge. */
export const FAIR_COOKIE_BANNER_BOTTOM_OFFSET_PX = 40
/** Mobile: full width minus 16px side margins. */
export const FAIR_COOKIE_BANNER_SIDE_MARGIN_PX = 16
/** Desktop card width (Figma 39:41638). */
export const FAIR_COOKIE_BANNER_DESKTOP_WIDTH_PX = 440

export const FAIR_COOKIE_CONSENT_CHOICES = ['accepted', 'essential'] as const
export type FairCookieConsentChoice = (typeof FAIR_COOKIE_CONSENT_CHOICES)[number]

export interface FairCookieConsentRecord {
  choice: FairCookieConsentChoice
  /** ISO date of the decision. */
  decidedAt: string
}

export function isFairCookieConsentChoice(value: unknown): value is FairCookieConsentChoice {
  return typeof value === 'string' && (FAIR_COOKIE_CONSENT_CHOICES as readonly string[]).includes(value)
}

export function parseFairCookieConsent(raw: string | null): FairCookieConsentRecord | null {
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    const { choice, decidedAt } = parsed as Partial<FairCookieConsentRecord>
    if (!isFairCookieConsentChoice(choice)) return null
    return { choice, decidedAt: typeof decidedAt === 'string' ? decidedAt : '' }
  } catch {
    return null
  }
}

export function serializeFairCookieConsent(record: FairCookieConsentRecord): string {
  return JSON.stringify(record)
}

function storage(): Storage | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage
  } catch {
    return null
  }
}

export function readFairCookieConsent(): FairCookieConsentRecord | null {
  const store = storage()
  if (!store) return null
  try {
    return parseFairCookieConsent(store.getItem(FAIR_COOKIE_CONSENT_STORAGE_KEY))
  } catch {
    return null
  }
}

export function writeFairCookieConsent(
  choice: FairCookieConsentChoice,
  now: Date = new Date(),
): FairCookieConsentRecord {
  const record: FairCookieConsentRecord = { choice, decidedAt: now.toISOString() }
  const store = storage()
  if (store) {
    try {
      store.setItem(FAIR_COOKIE_CONSENT_STORAGE_KEY, serializeFairCookieConsent(record))
    } catch {
      // Private mode / quota — the banner will simply come back next visit.
    }
  }
  return record
}

/**
 * Hook-up point for analytics / personal trackers: only after "Accept all".
 * No decision yet counts as "not allowed".
 */
export function isFairAnalyticsAllowed(record: FairCookieConsentRecord | null = readFairCookieConsent()): boolean {
  return record?.choice === 'accepted'
}
